<script lang="ts">
	import { t } from 'svelte-intl-precompile';
	import { createRadioGroup, melt } from '@melt-ui/svelte';

	import Toggle from '$lib/components/atoms/Toggle/Toggle.svelte';
	import OverflowTooltip from '$lib/components/atoms/OverflowTooltip/OverflowTooltip.svelte';
	import SecondLevelMenu from '../SecondLevelMenu/SecondLevelMenu.svelte';
	import IconClose from '$lib/assets/svg/icons/close.svg?component';

	import { getDefaultMenuItem } from '$lib/utils';
	import { isFilterMenuOpened } from '$store/filterMenuStore';
	import Button from '$lib/components/atoms/Button/Button.svelte';
	import { getMenuLink, type MenuItem } from '$lib/utils/menu';

	interface Props {
		selectedItem?: string | null;
		menu?: MenuItem[];
		useDefaultPage?: boolean;
		onChange?: (str: string) => void | boolean;
		onpagechange?: () => void;
		onClick?: (event: MouseEvent) => void;
	}

	let {
		selectedItem = null,
		menu = [] as MenuItem[],
		useDefaultPage = false,
		onChange,
		onpagechange,
		onClick
	}: Props = $props();

	const {
		elements: { root, item }
	} = createRadioGroup({
		onValueChange: ({ curr, next }) => {
			if (selectedItem === next) {
				return curr;
			}

			if (!onChange?.(next)) {
				return curr;
			}

			return next;
		},
		defaultValue: selectedItem || undefined
	});

	let firstLevelMenu = $derived(menu.filter((item) => item.level === 0));
	let secondLevelMenu = $derived(
		menu.filter((item) => item.level === 1 && item.parent === selectedItem)
	);
</script>

{#if $isFilterMenuOpened}
	<div class="FirstLevelMenu">
		<Button
			aria-label="Close"
			testid="Sidebar_close_filters"
			onclick={() => ($isFilterMenuOpened = false)}
			variant="iconOnly"
			icon={IconClose}
		></Button>
	</div>
{:else}
	<div use:melt={$root} class="FirstLevelMenu">
		{#each firstLevelMenu.filter((item) => item.position === 'top') as { name, icon } (name)}
			{@const menuName = $t(`menu.${name}`)}
			<div
				use:melt={$item(name)}
				onclick={onClick}
				id={name}
				aria-labelledby="{menuName}-label"
				data-testid={name}
			>
				{#if useDefaultPage}
					{@const defaultMenuItem = getDefaultMenuItem(name, menu)}
					{@const SvelteComponent = icon}
					<a
						href={getMenuLink(defaultMenuItem)}
						class="FirstLevelMenu__link"
						class:FirstLevelMenu__link--active={selectedItem === name}
						aria-current={selectedItem === name ? 'page' : null}
						aria-label={menuName}
					>
						<span class="FirstLevelMenu__icon">
							<SvelteComponent></SvelteComponent>
						</span>
						<span class="FirstLevelMenu__label">
							{#key menuName}
								<OverflowTooltip text={menuName}></OverflowTooltip>
							{/key}
						</span>
					</a>
				{:else}
					<Toggle
						ariaLabel="Icon"
						pressed={selectedItem === name}
						variant="submenu-item"
						testid={name}
					>
						{#snippet iconSlot()}
							{@const SvelteComponent_1 = icon}
							<span>
								<span><SvelteComponent_1></SvelteComponent_1></span>
							</span>
						{/snippet}
						{#snippet labelSlot()}
							<span>
								{#key menuName}
									<OverflowTooltip text={menuName}></OverflowTooltip>
								{/key}
							</span>
						{/snippet}
					</Toggle>
				{/if}
				{#if selectedItem === name}
					<div class="FirstLevelMenu__SecondLevelMenu-wrapper">
						<SecondLevelMenu menu={secondLevelMenu} onChange={() => onpagechange?.()}
						></SecondLevelMenu>
					</div>
				{/if}
			</div>
		{/each}
	</div>
	<div use:melt={$root} class="FirstLevelMenu--bottom">
		{#each firstLevelMenu.filter((item) => item.position === 'bottom') as { name, icon } (name)}
			{@const menuName = $t(`menu.${name}`)}
			<div
				use:melt={$item(name)}
				onclick={onClick}
				id={name}
				data-testid={name}
				aria-labelledby="{menuName}-label"
			>
				{#if useDefaultPage}
					{@const defaultMenuItem = getDefaultMenuItem(name, menu)}
					{@const SvelteComponent_2 = icon}
					<a
						href={getMenuLink(defaultMenuItem)}
						class="FirstLevelMenu__link"
						class:FirstLevelMenu__link--active={selectedItem === name}
						aria-current={selectedItem === name ? 'page' : null}
						aria-label={menuName}
					>
						<span class="FirstLevelMenu__icon">
							<SvelteComponent_2></SvelteComponent_2>
						</span>
						<span class="FirstLevelMenu__label">
							{#key menuName}
								<OverflowTooltip text={menuName}></OverflowTooltip>
							{/key}
						</span>
					</a>
				{:else}
					<Toggle
						ariaLabel="Icon"
						pressed={selectedItem === name}
						variant="submenu-item"
						testid={name}
					>
						{#snippet iconSlot()}
							{@const SvelteComponent_3 = icon}
							<span>
								<span><SvelteComponent_3></SvelteComponent_3></span>
							</span>
						{/snippet}
						{#snippet labelSlot()}
							<span>
								{#key menuName}
									<OverflowTooltip text={menuName}></OverflowTooltip>
								{/key}
							</span>
						{/snippet}
					</Toggle>
				{/if}
			</div>
			{#if selectedItem === name}
				<div class="FirstLevelMenu__SecondLevelMenu-wrapper">
					<SecondLevelMenu menu={secondLevelMenu} onChange={() => onpagechange?.()}
					></SecondLevelMenu>
				</div>
			{/if}
		{/each}
	</div>
{/if}

<style lang="scss" src="./FirstLevelMenu.scss"></style>
